// CourtScribe transcript PDF: same Judicial Service of Ghana letterhead as the
// summary (see courtPdfChrome.js), but the body is a conversation — each utterance
// carries its speaker and timestamp, rendered turn by turn.
import {
  COLORS,
  PdfWriter,
  getLogoDataUrl,
  renderLetterhead,
  jsPDF,
} from './courtPdfChrome';
import { getSpeakerDisplayName } from '../../utils/speakerSegments';

const NOT_STATED = 'Not stated';

const pad = (n) => String(n).padStart(2, '0');

const formatClock = (seconds) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s < 0) return '00:00:00';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
};

const cleanText = (value) =>
  String(value ?? '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/^#+\s*/, '')
    .trim();

/**
 * Build the transcript document model from speaker segments.
 * Each turn = { speaker, time, text }. Falls back to a single flowing turn when no
 * timed segments are available so the export still carries the letterhead.
 */
export const buildCourtTranscriptDocument = (
  transcription,
  segments,
  labelMap = {},
  { courtName = '', content = '' } = {},
) => {
  const usableSegments = Array.isArray(segments)
    ? segments.filter((seg) => cleanText(seg?.text))
    : [];

  const turns = usableSegments.map((seg) => ({
    speaker: cleanText(getSpeakerDisplayName(seg, labelMap)) || 'Speaker',
    time: formatClock(seg.start_time_seconds),
    text: cleanText(seg.text),
  }));

  // No timed speaker turns: render the plain transcript body as one block.
  if (turns.length === 0) {
    const body = cleanText(content || transcription?.content);
    if (!body) return null;
    turns.push({ speaker: 'Court', time: '00:00:00', text: body });
  }

  const caseTitle = cleanText(transcription?.title) || 'Court hearing';
  const suitNumber = cleanText(transcription?.case_reference) || 'Not applicable';

  const details = [
    ['Title of Case', caseTitle],
    ['Suit / Case No.', suitNumber],
    ['Court / Division', cleanText(courtName) || NOT_STATED],
    ['Hearing Date', transcription?.date || NOT_STATED],
    ['Duration', transcription?.duration && transcription.duration !== 'Unknown'
      ? transcription.duration
      : NOT_STATED],
    ['Language', transcription?.language || 'English'],
    ['Speakers', String(new Set(turns.map((t) => t.speaker)).size)],
  ];

  return {
    meta: {
      caseTitle,
      suitNumber,
      courtName: cleanText(courtName) || NOT_STATED,
      transcriptId: suitNumber !== 'Not applicable' ? suitNumber : caseTitle,
      documentTitle: 'COURT SCRIBE — AI-GENERATED TRANSCRIPT',
      bannerNotice:
        'For review by the Judge and Registrar only. Not an official record of the proceedings until verified and certified.',
      draftLabel: 'INTERNAL DRAFT — AI-GENERATED',
    },
    disclaimer:
      'This transcript was produced by an automated speech-to-text system. Speaker labels and timestamps are machine-generated and must be checked against the recording before any reliance is placed on them.',
    details,
    turns,
  };
};

/** Render the transcript model with the shared letterhead and conversation body. */
export const renderCourtTranscriptPdf = (model, { logoDataUrl } = {}) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  doc.setProperties({ title: `${model.meta.caseTitle} — AI-Generated Transcript` });
  const writer = new PdfWriter(doc);
  writer.startPage(model.meta);
  renderLetterhead(writer, doc, model.meta, logoDataUrl);

  writer.partBanner('HEARING DETAILS');
  writer.labelTable(model.details);

  writer.partBanner('TRANSCRIPT OF PROCEEDINGS');
  writer.text(model.disclaimer, { size: 8, style: 'italic', color: COLORS.muted });
  writer.y += 1;
  model.turns.forEach((turn) => writer.conversationTurn(turn));

  writer.finish();
  return doc;
};

/** Rasterize the brand logo and export the transcript as a PDF download. */
export const downloadCourtTranscriptPdf = async (model, filename) => {
  const logoDataUrl = await getLogoDataUrl();
  const doc = renderCourtTranscriptPdf(model, { logoDataUrl });
  doc.save(filename);
};
